import { PropTypes } from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import LeadDrawerActions from "~/actions/lead_drawer_actions";
import ActivityEditor from "~/components/forms/HtmlEditors/ActivityEditor";
import NoteTemplateSelect from "~/components/forms/Templates/NoteTemplateSelect";
import InsertDocumentButton from "~/components/insert_document_button";
import UnsupportedVariablesWarning from "~/components/modals/UnsupportedVariablesWarning";
import { supportsActivityVariables } from "~/constants/EditorTypes";
import { isAdminOrStaff } from "~/helpers/userRoleHelper";
import { normalizeUnsupportedVariablesError } from "~/lib/unsupportedVariables";
import FormFooter from "./common/form_footer";

const validateForm = (activityType, body, footerRef) => {
  const errors = {};
  if (activityType === "note" && _lodash.isEmpty(body)) {
    errors.body = "Can't be empty";
  }
  return footerRef.current.validate(errors);
};

const handleFormSubmit = (
  e,
  validate,
  id,
  body,
  callSid,
  activityType,
  lead,
  footerRef,
  setErrors,
  saveAnyway,
  submitOptions,
) => {
  e.preventDefault();
  const validationErrors = validate();

  if (_lodash.isEmpty(validationErrors)) {
    const footer = footerRef.current.serialize();
    const { status, rating, task, notify, call_disposition, internal } = footer;

    const data = {
      status,
      rating,
      task,
      event: activityType,
      save_anyway: saveAnyway,
      activity: {
        body,
        internal,
        notify,
        call_sid: callSid,
      },
    };

    if (call_disposition) {
      data.activity.parameters = {
        call_disposition,
      };
    }

    if (id) {
      LeadDrawerActions.updateLeadActivity(lead, id, data, {
        ...submitOptions,
      });
    } else {
      LeadDrawerActions.createLeadActivity(lead, data, {
        ...submitOptions,
      });
    }
  } else {
    setErrors(validationErrors);
  }
};

const NoteForm = ({
  lead,
  dialer = {},
  activityType = "note",
  submitButtonText = "Save Note",
  submitButtonSendingText = "Saving ...",
}) => {
  const [id, setId] = useState(dialer.id || null);
  const [body, setBody] = useState(dialer.body || "");
  const [callSid, setCallSid] = useState(dialer.callSid || "");
  const [callStatus, setCallStatus] = useState(dialer.callStatus || "");
  const [internal, setInternal] = useState(
    GlobalContainer.product() === "retention",
  );
  const [errors, setErrors] = useState({});
  const [variablesWarning, setVariablesWarning] = useState(null);
  const [submittingSaveAnyway, setSubmittingSaveAnyway] = useState(false);
  const [footerSending, setFooterSending] = useState(false);
  const [linksToInsert, setLinksToInsert] = useState([]);
  const [templateBody, setTemplateBody] = useState(null);
  const footerRef = useRef();
  const variablesEnabled =
    isAdminOrStaff() && supportsActivityVariables(activityType);

  const validate = useCallback(
    () => validateForm(activityType, body, footerRef),
    [activityType, body, footerRef],
  );

  const onFormSubmit = useCallback(
    (e, saveAnyway = false) =>
      handleFormSubmit(
        e,
        validate,
        id,
        body,
        callSid,
        activityType,
        lead,
        footerRef,
        setErrors,
        saveAnyway,
        {
          onUnsupportedVariables: (warning) => {
            setSubmittingSaveAnyway(false);
            setFooterSending(false);
            footerRef.current?.resetSending?.();
            setVariablesWarning(normalizeUnsupportedVariablesError(warning));
          },
          onDone: () => {
            setSubmittingSaveAnyway(false);
            setFooterSending(false);
            footerRef.current?.resetSending?.();
            setVariablesWarning(null);
          },
          onFail: () => {
            setSubmittingSaveAnyway(false);
            setFooterSending(false);
            footerRef.current?.resetSending?.();
            setVariablesWarning(null);
            GlobalContainer.notify(
              "Failed to save note. Please try again.",
              "error",
            );
          },
        },
      ),
    [validate, id, body, callSid, activityType, lead, footerRef],
  );

  useEffect(() => {
    setId(dialer.id);
    setBody(dialer.body);
    setCallSid(dialer.callSid);
    setCallStatus(dialer.callStatus);
  }, [dialer]);

  const applyTemplate = useCallback((template) => {
    if (template) {
      setBody(template.body || "");
      setTemplateBody(template.body || "");
      setErrors({});
    } else {
      setBody("");
      setTemplateBody(null);
      setErrors({});
    }
  }, []);

  return (
    <>
      <form onSubmit={onFormSubmit}>
        <div className="form-group mb15">
          <ActivityEditor
            value={body || ""}
            className={errors.body ? "tiny-mce has-error" : ""}
            onChange={setBody}
            placeholder="Enter Text"
            activityType={activityType}
            leadId={lead.id}
            spellCheck
            enableVariables={variablesEnabled}
            key={activityType}
            linksToInsert={linksToInsert}
            clearLinksToInsert={() => setLinksToInsert([])}
            replaceValue={templateBody}
            clearReplaceValue={() => setTemplateBody(null)}
          />
        </div>
        <div className="form-row">
          <div className="form-group col-lg-4">
            <InsertDocumentButton setLinksToInsert={setLinksToInsert} />
          </div>
          <div className="form-group col-lg-4 ml-auto">
            <NoteTemplateSelect applyTemplate={applyTemplate} body={body} />
          </div>
        </div>
        <div className="mt15 mb15">
          <FormFooter
            ref={footerRef}
            dialer={dialer}
            activityType={activityType}
            status={lead.status}
            callStatus={callStatus}
            internal={internal}
            lead={lead}
            onSubmit={onFormSubmit}
            sending={footerSending}
            submitButtonText={id ? "Save" : submitButtonText}
            submitButtonSendingText={submitButtonSendingText}
          />
        </div>
      </form>
      <UnsupportedVariablesWarning
        show={!!variablesWarning}
        warning={variablesWarning}
        saving={submittingSaveAnyway}
        onReview={() => setVariablesWarning(null)}
        onSaveAnyway={() => {
          setSubmittingSaveAnyway(true);
          setFooterSending(true);
          onFormSubmit({ preventDefault: () => false }, true);
        }}
      />
    </>
  );
};

NoteForm.propTypes = {
  lead: PropTypes.shape({}).isRequired,
  dialer: PropTypes.shape({}),
  activityType: PropTypes.string,
  submitButtonText: PropTypes.string,
  submitButtonSendingText: PropTypes.string,
};

export default NoteForm;
